import { CLAVES } from './sesion.store';

/**
 * Traspaso de sesión **entre pestañas del mismo origen**.
 *
 * Si esta pestaña fue abierta con `window.open()` desde la propia aplicación y
 * todavía no tiene sesión, copia el `sessionStorage` del `opener`. Se ejecuta en
 * `main.ts`, **antes** del bootstrap de Angular y por lo tanto antes del guard.
 *
 * Copiado de `gestion-web-cliente` (`modulos/login/session-handoff.ts`).
 *
 * Ojo con el alcance: con FARO en dominio propio (ADR-010), esto **no** sirve
 * para venir desde IRIX — `opener.sessionStorage` cross-origin lanza
 * `SecurityError`. Sí sirve dentro de FARO, que abre pestañas de sí mismo (el
 * detalle de una luminaria en otra pestaña, por ejemplo). Para venir de IRIX
 * está el código de un solo uso de más abajo.
 */
export function copiarSesionDesdeOpener(): void {
  try {
    // Ya hay sesión propia: no pisarla.
    if (sessionStorage.getItem(CLAVES.accessToken)) return;

    const opener: Window | null = window.opener;
    // Pestaña abierta a mano, clic del medio o `rel=noopener`.
    if (!opener) return;

    // Si el opener es de otro origen, esta línea lanza SecurityError.
    const origen = opener.sessionStorage;
    if (!origen.getItem(CLAVES.accessToken)) return;

    for (let i = 0; i < origen.length; i++) {
      const clave = origen.key(i);
      if (clave !== null) sessionStorage.setItem(clave, origen.getItem(clave) ?? '');
    }

    // Una sola vez: evita recopiar la sesión si esta pestaña cierra sesión y recarga.
    window.opener = null;
  } catch {
    /* opener cross-origin o almacenamiento inaccesible: sigue sin sesión */
  }
}

/** Canal entre pestañas de FARO. `BroadcastChannel` no cruza orígenes. */
const CANAL_SESION = 'faro-sesion';

/** Cuánto espera una pestaña nueva a que otra le preste la sesión. */
const ESPERA_SESION_MS = 300;

type MensajeSesion = { tipo: 'pedido' } | { tipo: 'sesion'; datos: Record<string, string> };

/**
 * Contesta a las pestañas nuevas que piden la sesión.
 *
 * Cubre lo que `copiarSesionDesdeOpener` no alcanza: Ctrl+clic, clic del medio,
 * "Abrir en una pestaña nueva" o pegar la URL. En todos esos casos la pestaña
 * nace sin `opener` y con el `sessionStorage` vacío. Se llama en `main.ts` y
 * queda escuchando toda la vida de la pestaña.
 *
 * Sólo se prestan las claves de `CLAVES`: descargas y memoria de solapas son
 * de cada pestaña.
 */
export function atenderPedidosDeSesion(): void {
  try {
    const canal = new BroadcastChannel(CANAL_SESION);
    canal.onmessage = (evento: MessageEvent<MensajeSesion>) => {
      if (evento.data?.tipo !== 'pedido') return;
      try {
        // Sólo una sesión completa: con token **y** permiso elegido. Una a medio
        // armar (en el login, en /sin-acceso) mandaría a la nueva a /sin-acceso.
        if (!sessionStorage.getItem(CLAVES.accessToken)) return;
        if (!sessionStorage.getItem(CLAVES.permisoActual)) return;
        const datos: Record<string, string> = {};
        for (const clave of Object.values(CLAVES)) {
          const valor = sessionStorage.getItem(clave);
          if (valor !== null) datos[clave] = valor;
        }
        canal.postMessage({ tipo: 'sesion', datos } satisfies MensajeSesion);
      } catch {
        /* almacenamiento inaccesible: no hay nada que prestar */
      }
    };
  } catch {
    /* navegador sin BroadcastChannel */
  }
}

/**
 * Si esta pestaña no tiene sesión, se la pide a otra pestaña de FARO abierta.
 *
 * Toma la primera respuesta y, si nadie contesta en `ESPERA_SESION_MS`, sigue
 * sin sesión (y el guard lleva al login, como siempre). La espera sólo la paga
 * quien entra sin sesión; con sesión propia vuelve enseguida.
 */
export function pedirSesionAOtraPestana(): Promise<void> {
  try {
    if (sessionStorage.getItem(CLAVES.accessToken)) return Promise.resolve();
  } catch {
    return Promise.resolve();
  }

  return new Promise((resolver) => {
    let canal: BroadcastChannel;
    try {
      canal = new BroadcastChannel(CANAL_SESION);
    } catch {
      resolver();
      return;
    }

    const terminar = () => {
      clearTimeout(plazo);
      canal.close();
      resolver();
    };
    const plazo = setTimeout(terminar, ESPERA_SESION_MS);

    canal.onmessage = (evento: MessageEvent<MensajeSesion>) => {
      const mensaje = evento.data;
      if (mensaje?.tipo !== 'sesion' || !mensaje.datos?.[CLAVES.accessToken]) return;
      try {
        // Otra respuesta, o un login en esta misma pestaña, llegó antes.
        if (!sessionStorage.getItem(CLAVES.accessToken)) {
          for (const [clave, valor] of Object.entries(mensaje.datos)) {
            sessionStorage.setItem(clave, valor);
          }
        }
      } catch {
        /* almacenamiento inaccesible: sigue sin sesión */
      }
      terminar();
    };

    canal.postMessage({ tipo: 'pedido' } satisfies MensajeSesion);
  });
}

/** Nombre del parámetro del fragmento que trae el código de traspaso. */
const PARAMETRO_HANDOFF = 'handoff';

/**
 * Lee el código de traspaso del **fragmento** de la URL y lo borra.
 *
 * Va en el fragmento (`#handoff=…`) y no en la query a propósito: el fragmento
 * no se manda al servidor, así que no queda en los logs de acceso ni viaja en el
 * `Referer`.
 *
 * Devuelve `undefined` si no hay código, que es el caso normal.
 */
export function tomarCodigoDeHandoff(): string | undefined {
  try {
    const fragmento = window.location.hash.replace(/^#/, '');
    if (!fragmento) return undefined;

    const params = new URLSearchParams(fragmento);
    const codigo = params.get(PARAMETRO_HANDOFF);
    if (!codigo) return undefined;

    // Se limpia la URL enseguida: el código es de un solo uso y no tiene por qué
    // quedar visible ni en el historial.
    params.delete(PARAMETRO_HANDOFF);
    const resto = params.toString();
    window.history.replaceState(
      null,
      '',
      window.location.pathname + window.location.search + (resto ? `#${resto}` : ''),
    );

    return codigo;
  } catch {
    return undefined;
  }
}
